import { supabase } from '../supabaseClient';

export interface ProjectAccess {
  id: string;
  client_id: string;
  project_id: string;
  credits_spent: number;
  transaction_id?: string;
  created_at: string;
}

export interface ProjectAccessRequest {
  project_id: string;
  credits_spent: number;
}

export interface ServiceAccessRequest {
  service_id: string;
  credits_spent: number;
}

// Check if user has access to project details
export const hasProjectAccess = async (projectId: string): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('contact_access')
      .select('id')
      .eq('client_id', user.id)
      .eq('project_id', projectId)
      .maybeSingle();

    if (error) {
      console.error('Error checking project access:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking project access:', error);
    return false;
  }
};

// Purchase access to project details
export const purchaseProjectAccess = async (request: ProjectAccessRequest): Promise<ProjectAccess> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Check if user already has access
    const hasAccess = await hasProjectAccess(request.project_id);
    if (hasAccess) {
      throw new Error('Вече имате достъп до този проект');
    }

    // Check user credits
    const { data: creditsData, error: creditsError } = await supabase
      .from('user_credits')
      .select('credits_balance')
      .eq('user_id', user.id)
      .single();

    if (creditsError || !creditsData) {
      throw new Error('Не може да се проверят кредитите на потребителя');
    }

    if (creditsData.credits_balance < request.credits_spent) {
      throw new Error(`Недостатъчно кредити. Нужни: ${request.credits_spent}, налични: ${creditsData.credits_balance}`);
    }

    // Get project owner
    const { data: projectData, error: projectError } = await supabase
      .from('projects')
      .select('client_id')
      .eq('id', request.project_id)
      .single();

    if (projectError || !projectData) {
      throw new Error('Проектът не е намерен');
    }

    // Create transaction record
    const { data: transactionData, error: transactionError } = await supabase
      .from('credit_transactions')
      .insert({
        user_id: user.id,
        amount: -request.credits_spent,
        description: `Project access purchase - ${request.project_id}`,
        type: 'USAGE'
      })
      .select()
      .single();

    if (transactionError) {
      console.error('Transaction error:', transactionError);
      throw new Error('Неуспешно създаване на записа за транзакция');
    }

    // Create access record
    const { data: accessData, error: accessError } = await supabase
      .from('contact_access')
      .insert({
        client_id: user.id,
        provider_id: null, // For project access, provider_id should be null
        project_id: request.project_id,
        credits_spent: request.credits_spent,
        transaction_id: transactionData.id
      })
      .select()
      .single();

    if (accessError) {
      console.error('Access record error:', accessError);
      // Rollback transaction
      await supabase
        .from('credit_transactions')
        .delete()
        .eq('id', transactionData.id);
      
      throw new Error('Неуспешно създаване на записа за достъп');
    }

    // Deduct credits from user
    const { error: updateError } = await supabase
      .from('user_credits')
      .update({ 
        credits_balance: creditsData.credits_balance - request.credits_spent,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', user.id);

    if (updateError) {
      console.error('Credit update error:', updateError);
      // Rollback everything
      await supabase
        .from('contact_access')
        .delete()
        .eq('id', accessData.id);
      await supabase
        .from('credit_transactions')
        .delete()
        .eq('id', transactionData.id);
      
      throw new Error('Неуспешно приспадане на кредити');
    }

    return accessData;
  } catch (error) {
    console.error('Error purchasing project access:', error);
    throw error;
  }
};

// Get project access history for user
export const getUserProjectAccess = async (): Promise<ProjectAccess[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('contact_access')
      .select(`
        id,
        client_id,
        project_id,
        credits_spent,
        created_at,
        projects!inner(
          id,
          title,
          client_id
        )
      `)
      .eq('client_id', user.id)
      .not('project_id', 'is', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching project access:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching project access:', error);
    return [];
  }
};

// Get project owner info (only if user has access)
export const getProjectOwnerInfo = async (projectId: string) => {
  try {
    const hasAccess = await hasProjectAccess(projectId);
    if (!hasAccess) {
      throw new Error('No access to project details');
    }

    const { data, error } = await supabase
      .from('projects')
      .select(`
        id,
        title,
        description,
        budget_min,
        budget_max,
        deadline,
        skills,
        created_at,
        client:profiles!projects_client_id_fkey(
          id,
          full_name,
          email,
          phone,
          company,
          avatar_url
        )
      `)
      .eq('id', projectId)
      .single();

    if (error) {
      throw new Error('Проектът не е намерен');
    }

    return data;
  } catch (error) {
    console.error('Error fetching project owner info:', error);
    throw error;
  }
};

// Check if user has access to service provider details
export const hasServiceAccess = async (serviceId: string): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Check if user has access to this specific service
    const { data, error } = await supabase
      .from('contact_access')
      .select('id')
      .eq('client_id', user.id)
      .eq('service_id', serviceId)
      .maybeSingle();

    if (error) {
      console.error('Error checking service access:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking service access:', error);
    return false;
  }
};

// Purchase access to service provider details
export const purchaseServiceAccess = async (request: ServiceAccessRequest): Promise<any> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    // Check if user already has access
    const hasAccess = await hasServiceAccess(request.service_id);
    if (hasAccess) {
      throw new Error('Вече имате достъп до данните на този изпълнител');
    }

    // Check user credits
    const { data: creditsData, error: creditsError } = await supabase
      .from('user_credits')
      .select('credits_balance')
      .eq('user_id', user.id)
      .single();

    if (creditsError || !creditsData) {
      throw new Error('Не може да се проверят кредитите на потребителя');
    }

    if (creditsData.credits_balance < request.credits_spent) {
      throw new Error(`Недостатъчно кредити. Нужни: ${request.credits_spent}, налични: ${creditsData.credits_balance}`);
    }

    // Get service provider
    const { data: serviceData, error: serviceError } = await supabase
      .from('services')
      .select('provider_id')
      .eq('id', request.service_id)
      .single();

    if (serviceError || !serviceData) {
      throw new Error('Услугата не е намерена');
    }

    // Create transaction record
    const { data: transactionData, error: transactionError } = await supabase
      .from('credit_transactions')
      .insert({
        user_id: user.id,
        amount: -request.credits_spent,
        description: `Service executor access purchase - ${request.service_id}`,
        type: 'USAGE'
      })
      .select()
      .single();

    if (transactionError) {
      console.error('Transaction error:', transactionError);
      throw new Error('Неуспешно създаване на записа за транзакция');
    }

    // Create access record with service_id
    const { data: accessData, error: accessError } = await supabase
      .from('contact_access')
      .insert({
        client_id: user.id,
        provider_id: serviceData.provider_id,
        service_id: request.service_id,
        credits_spent: request.credits_spent,
        transaction_id: transactionData.id
      })
      .select()
      .single();

    if (accessError) {
      console.error('Access record error:', accessError);
      throw new Error('Неуспешно създаване на записа за достъп');
    }

    // Update user credits
    const { error: updateError } = await supabase
      .from('user_credits')
      .update({
        credits_balance: creditsData.credits_balance - request.credits_spent
      })
      .eq('user_id', user.id);

    if (updateError) {
      console.error('Credit update error:', updateError);
      throw new Error('Неуспешно обновяване на кредитите');
    }

    return accessData;
  } catch (error) {
    console.error('Error purchasing service access:', error);
    throw error;
  }
};

// Get service provider info (only if user has access)
export const getServiceProviderInfo = async (serviceId: string) => {
  try {
    const hasAccess = await hasServiceAccess(serviceId);
    if (!hasAccess) {
      throw new Error('No access to service executor details');
    }

    const { data, error } = await supabase
      .from('services')
      .select(`
        id,
        title,
        description,
        price,
        price_type,
        delivery_days,
        skills,
        created_at,
        provider:profiles!services_provider_id_fkey(
          id,
          full_name,
          email,
          phone,
          company,
          avatar_url,
          rating_avg,
          rating_cnt,
          verified
        )
      `)
      .eq('id', serviceId)
      .single();

    if (error) {
      throw new Error('Услугата не е намерена');
    }

    return data;
  } catch (error) {
    console.error('Error fetching service provider info:', error);
    throw error;
  }
};
